import axios from "axios";
import { get, isEmpty } from "lodash";
import { toastr } from "react-redux-toastr";
import { Group } from "../../utils/logger";

import {
  USER_TOKEN_NAME,
  ADMIN_ERROR,
  ADMIN_LOADING,
  STORE_USER,
  LOGIN_USER,
  SET_USERS,
  SELECT_USER,
  SET_APPROVERS,
  SELECT_APPROVER,
  SET_SEEDS,
  SELECT_SEED,
  SET_WHITELIST,
  SELECT_WHITELIST,
  SET_SUPPRESSIONLIST,
  SELECT_SUPPRESSIONLIST,
  // Reset for impersonation
  RESET_DATASETS,
  RESET_CAMPAIGNS,
} from "../constants";
import { loadUser } from "./userActions";

const LoggerGroup = new Group("Admin Actions", { disable: true });

export const adminLoading = (type) => (dispatch) => {
  dispatch({ type: ADMIN_LOADING, payload: type });
};

// ================================================================================================
// User Operations ================================================================================
// ================================================================================================

export const selectUser = (approver) => (dispatch) => {
  dispatch({ type: SELECT_USER, payload: approver });
};

export const storeAdminUser = (payload) => (dispatch) => {
  dispatch({ type: STORE_USER, payload });
};

let usersCache;
export const getUsers =
  (force_refresh, include = "active") =>
  (dispatch) => {
    if (!usersCache || force_refresh) {
      dispatch(adminLoading("users"));

      LoggerGroup.emit("[i] Updating list of users");

      usersCache = axios({
        url: `admin/users`,
        method: "get",
        params: { include },
      })
        .then((res) => {
          const users = get(res, "data.users");
          if (users) {
            LoggerGroup.emit("  [i] List of users", { table: users }).end();
            dispatch({ type: SET_USERS, payload: users });
          }
        })
        .catch((err) => {
          LoggerGroup.emit({ warn: "[i] Error getting users" }, { dir: err.response }).end();
          dispatch({ type: ADMIN_ERROR, payload: { users: "Error getting users" } });
        });
    }

    return usersCache;
  };

export const impersonateUser = (as_user_id) => (dispatch, getState) => {
  const { token, selectedUser } = getState().admin;

  LoggerGroup.emit(`[i] Admin[${selectedUser.user_id_bint}] impersonating user[${as_user_id}]`);

  // Clear old errors
  dispatch({ type: ADMIN_ERROR, payload: { impersonate: null } });

  if (as_user_id === selectedUser.user_id_bint) {
    LoggerGroup.emit("Switching back to current user").end();
    return updateUser({ user: selectedUser, token });
  }

  return axios({
    url: `admin/impersonate`,
    method: "post",
    data: { as_user_id },
  })
    .then((res) => updateUser(res.data))
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error impersonating user[${as_user_id}]` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { impersonate: error_message } });
    });

  function updateUser(payload) {
    if (localStorage.getItem(USER_TOKEN_NAME)) {
      payload.rememberMe = true;
    }

    LoggerGroup.end();

    dispatch({ type: RESET_DATASETS });
    dispatch({ type: RESET_CAMPAIGNS });
    dispatch({ type: LOGIN_USER, payload }); // Save token to state
    dispatch(loadUser(payload));
    window.location.reload(); // Reload to re-trigger all componentDidMount initializations
  }
};

export const updateUserPermissions = (user) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating user[${user.user_id_bint}] permissions`);

  return axios
    .patch(`admin/permissions/${user.user_id_bint}`, user)
    .then((res) => {
      const updatedUser = get(res, "data.user");
      if (updatedUser) {
        LoggerGroup.emit("  [i] Updated user", { table: updatedUser }).end();

        dispatch(getUsers(true));
        dispatch({ type: SELECT_USER, payload: user });
        toastr.success("Success", `Updated permissions for "${updatedUser.firstname_vch}"`);

        return updatedUser;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error updating user[${user.user_id_bint}] permissions` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { updatePermission: "Error updating user" } });
    });
};

export const deleteUser = (user) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting user[${user.user_id_bint}]`);

  return axios
    .delete(`admin/user/${user.user_id_bint}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(`  [i] Deleted user[${user.user_id_bint}]`).end();

        dispatch(getUsers(true));
        toastr.success("Success", `Deleted user: [${user.firstname_vch}]`);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error deleting user[${user.user_id_bint}]` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting user" } });
    });
};

// ================================================================================================
// Approver Operations ============================================================================
// ================================================================================================

export const selectApprover = (approver) => (dispatch) => {
  dispatch({ type: SELECT_APPROVER, payload: approver });
};

let approversCache;
export const getApprovers = (force_refresh) => (dispatch, getState) => {
  const { admin } = getState();

  if (isEmpty(admin.approvers) || force_refresh) {
    dispatch(adminLoading("approvers"));

    LoggerGroup.emit("[i] Updating list of approvers");

    approversCache = axios
      .get("admin/approvers")
      .then((res) => {
        const approvers = get(res, "data.approvers");
        if (approvers) {
          LoggerGroup.emit("  [i] List of approvers", { table: approvers }).end();
          dispatch({ type: SET_APPROVERS, payload: approvers });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting approvers" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { approvers: "Error getting approvers" } });
      });
  }

  return approversCache;
};

export const createApprover = (approver) => (dispatch) => {
  LoggerGroup.emit("[i] Creating approver");

  return axios
    .put("admin/approver", approver)
    .then((res) => {
      const newApprover = get(res, "data.approver");
      if (approver) {
        LoggerGroup.emit("  [i] Created approver", { table: newApprover }).end();

        dispatch(getApprovers(true));
        dispatch({ type: SELECT_APPROVER, payload: newApprover });
        toastr.success("Success", `${newApprover.firstname_vch} registered as approver`);

        return newApprover;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error getting approvers" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error getting approvers" } });
    });
};

export const updateApprover = (approver) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating approver[${approver.approverId_bint}]`);

  return axios
    .patch(`admin/approver/${approver.approverId_bint}`, approver)
    .then((res) => {
      const newApprover = get(res, "data.approver");
      if (newApprover) {
        LoggerGroup.emit("  [i] Updated approver", { table: newApprover }).end();

        dispatch(getApprovers(true));
        dispatch({ type: SELECT_APPROVER, payload: newApprover });
        toastr.success("Success", `Updated approver: ${newApprover.firstname_vch}`);

        return newApprover;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error updating approver[${approver.approverId_bint}]` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error updating approver" } });
    });
};

export const deleteApprover = (approver) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting approver[${approver.approverId_bint}]`);

  return axios
    .delete(`admin/approver/${approver.approverId_bint}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(`  [i] Deleted approver[${approver.approverId_bint}]`).end();

        dispatch(getApprovers(true));
        dispatch({ type: SELECT_APPROVER, payload: {} });
        toastr.success("Success", `Deleted approver: [${approver.firstname_vch}]`);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: `[i] Error deleting approver[${approver.approverId_bint}]` }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting approver" } });
    });
};

// ================================================================================================
// Seed List Operations ===========================================================================
// ================================================================================================

export const selectSeedList = (approver) => (dispatch) => {
  dispatch({ type: SELECT_SEED, payload: approver });
};

let seedListsCache;
export const getSeedLists = (force_refresh) => (dispatch, getState) => {
  const { admin } = getState();

  if (isEmpty(admin.seeds) || force_refresh) {
    dispatch(adminLoading("seedLists"));

    LoggerGroup.emit("[i] Updating list of seeds");

    seedListsCache = axios
      .get("admin/seed-lists")
      .then((res) => {
        const seedLists = get(res, "data.seedLists");
        if (seedLists) {
          LoggerGroup.emit("  [i] List of seeds", { table: seedLists }).end();
          dispatch({ type: SET_SEEDS, payload: seedLists });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting seeds" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { delete: "Error getting seeds" } });
      });
  }

  return seedListsCache;
};

export const createSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit("[i] Creating Seed List");

  return axios
    .put("admin/seed-list", seedList)
    .then((res) => {
      const newSeedList = get(res, "data.seedList");
      if (newSeedList) {
        LoggerGroup.emit("  [i] Created Seed List", { table: newSeedList }).end();

        dispatch(getSeedLists(true));
        dispatch({ type: SELECT_SEED, payload: newSeedList });
        toastr.success("Success", `Created Seed List "${newSeedList.name_vch}"`);

        return newSeedList;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error creating Seed List" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error creating Seed List" } });
    });
};

export const updateSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating Seed List[${seedList.seed_list_id_bint}]`);

  return axios
    .patch(`admin/seed-list/${seedList.seed_list_id_bint}`, seedList)
    .then((res) => {
      const newSeedList = get(res, "data.seedList");
      if (newSeedList) {
        LoggerGroup.emit("  [i] Updated seed", { table: newSeedList }).end();

        dispatch(getSeedLists(true));
        dispatch({ type: SELECT_SEED, payload: newSeedList });
        toastr.success("Success", `Updated seed: ${newSeedList.name_vch}`);

        return newSeedList;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error updating Seed List[${seedList.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error updating Seed List" } });
    });
};

export const deleteSeedList = (seedList) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting Seed List[${seedList.seed_list_id_bint}]`);

  return axios
    .delete(`admin/seed-list/${seedList.seed_list_id_bint}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(`  [i] Deleted Seed List[${seedList.seed_list_id_bint}]`).end();

        dispatch(getSeedLists(true));
        toastr.success("Success", `Deleted Seed List: ${seedList.name_vch}]`);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error deleting Seed List[${seedList.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting Seed List" } });
    });
};

// ================================================================================================
// Seed List Operations ===========================================================================
// ================================================================================================

export const selectWhitelist = (approver) => (dispatch) => {
  dispatch({ type: SELECT_WHITELIST, payload: approver });
};

let whitelistCache;
export const getWhitelist = (force_refresh) => (dispatch, getState) => {
  const { admin } = getState();

  if (isEmpty(admin.whitelist) || force_refresh) {
    dispatch(adminLoading("whitelist"));

    LoggerGroup.emit("[i] Updating whitelist");

    whitelistCache = axios
      .get("admin/whitelist")
      .then((res) => {
        const payload = get(res, "data");
        if (payload.whitelist) {
          LoggerGroup.emit("  [i Whitelist ", { table: payload.whitelist }).end();
          dispatch({ type: SET_WHITELIST, payload });
        }
      })
      .catch((err) => {
        LoggerGroup.emit({ warn: "[i] Error getting whitelist" }, { dir: err.response }).end();
        dispatch({ type: ADMIN_ERROR, payload: { whitelist: "Error getting whitelist" } });
      });
  }

  return whitelistCache;
};

export const createWhitelist = (whitelist) => (dispatch) => {
  LoggerGroup.emit("[i] Creating Whitelist");

  return axios
    .put("admin/whitelist", whitelist)
    .then((res) => {
      const newWhitelist = get(res, "data.whitelist");
      if (newWhitelist) {
        LoggerGroup.emit("  [i] Created Whitelist", { table: newWhitelist }).end();

        dispatch(getWhitelist(true));
        dispatch({ type: SELECT_WHITELIST, payload: newWhitelist });
        toastr.success("Success", `Created Whitelist record "${newWhitelist.name_vch}"`);

        return newWhitelist;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error adding Whitelist record" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error adding Whitelist record" } });
    });
};

export const updateWhitelist = (whitelist) => (dispatch) => {
  LoggerGroup.emit(`[i] Updating Whitelist[${whitelist.whitelist_id_bint}]`);

  return axios
    .patch(`admin/whitelist/${whitelist.whitelist_id_bint}`, whitelist)
    .then((res) => {
      const newWhitelist = get(res, "data.whitelist");
      if (newWhitelist) {
        LoggerGroup.emit("  [i] Updated whitelist record", { table: newWhitelist }).end();

        dispatch(getWhitelist(true));
        dispatch({ type: SELECT_WHITELIST, payload: newWhitelist });
        toastr.success("Success", `Updated whitelist: ${newWhitelist.name_vch}`);

        return newWhitelist;
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error updating Whitelist[${whitelist.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error updating Whitelist" } });
    });
};

export const deleteWhitelist = (whitelist) => (dispatch) => {
  LoggerGroup.emit(`[i] Deleting Whitelist[${whitelist.whitelist_id_bint}]`);

  return axios
    .delete(`admin/whitelist/${whitelist.whitelist_id_bint}`)
    .then((res) => {
      const status = get(res, "data.status");
      if (status === "success") {
        LoggerGroup.emit(
          `  [i] Deleted record from Whitelist[${whitelist.whitelist_id_bint}]`
        ).end();

        dispatch(getWhitelist(true));
        toastr.success("Success", `Deleted record from Whitelist: ${whitelist.name_vch}]`);
      }
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({
        warn: `[i] Error deleting Whitelist[${whitelist.seed_list_id_bint}]`,
      }).end();

      dispatch({ type: ADMIN_ERROR, payload: { delete: "Error deleting record from Whitelist" } });
    });
};

// -- SUPPRESSIONLIST

export const selectSuppressionlist = (approver) => (dispatch) => {
  dispatch({ type: SELECT_SUPPRESSIONLIST, payload: approver });
};

let suppressionlistCache;
export const clearSuppressionlist = () => (dispatch) => {
  dispatch({
    type: SET_SUPPRESSIONLIST,
    payload: {
      suppressionlist: [],
      searchsuppression: {},
    },
  });
};

export const getSuppressionlist = (force_refresh, searchRecord) => (dispatch, getState) => {
  try {
    const { admin } = getState();
    const pagination = {
      page: searchRecord.page || 1,
      limit: searchRecord.limit || 5,
      sortorder: searchRecord.sortorder,
      sortby: searchRecord.sortby,
    };

    if (isEmpty(admin.suppressionlist) || force_refresh) {
      dispatch(adminLoading("suppressionlist"));
      LoggerGroup.emit("[i] Fetching suppressionlist");
      // { from, to, received_at, action, type }
      let payload = {};
      let searchType =
        searchRecord && searchRecord.search_device_type ? searchRecord.search_device_type : "";
      let searchText =
        searchRecord && searchRecord.search_device_text ? searchRecord.search_device_text : "";

      switch (searchType.toLowerCase()) {
        case "email":
          payload = { to: searchText, type: "email", ...pagination };
          break;
        case "sms":
          payload = { from: searchText, type: "sms", ...pagination };
          break;
        case "voice":
          payload = { from: searchText, type: "voice", ...pagination };
          break;
        default:
          dispatch({
            type: ADMIN_ERROR,
            payload: {
              suppressionlist: "Error getting suppressionlist for searched device category",
            },
          });
          return suppressionlistCache;
      }

      suppressionlistCache = axios
        .post("/reporting/getoptoutlist/", payload)
        .then((res) => {
          const searchList = get(res, "data.results");
          const searchCount = get(res, "data.count");
          LoggerGroup.emit("  [i] suppressionlist AdminActions [searchList] ", {
            table: searchList,
          }).end();
          LoggerGroup.emit("  [i] suppressionlist AdminActions [searchCount] ", {
            table: searchCount,
          }).end();
          dispatch({
            type: SET_SUPPRESSIONLIST,
            payload: {
              suppressionlist: searchList || [],
              searchsuppression: searchRecord,
              searchCount: searchCount || 0,
            },
          });
          if (!searchList || searchList.length == 0) {
            toastr.error("Error", `No records found`);
          }
          dispatch({ type: ADMIN_ERROR, payload: { suppressionlist: "No record found" } });
        })
        .catch((err) => {
          LoggerGroup.emit(
            { warn: "[i] Error getting suppressionlist" },
            { dir: err.response }
          ).end();
          if (err.response && err.response.status && err.response.status === 404) {
            toastr.error(
              "Error",
              `No records found for searched ${searchRecord.search_device_type} opt-out criteria.`
            );
            dispatch({ type: ADMIN_ERROR, payload: { suppressionlist: "No record found" } });
          } else {
            toastr.error("Error", `Error getting suppressionlist.`);
            dispatch({
              type: ADMIN_ERROR,
              payload: { suppressionlist: "Error getting suppressionlist" },
            });
          }
          dispatch({
            type: SET_SUPPRESSIONLIST,
            payload: { suppressionlist: [], searchsuppression: searchRecord, searchCount: 0 },
          });
        });
    }
  } catch (ex) {
    LoggerGroup.emit(
      { warn: "[i] Error getting suppressionlist main failure" },
      { dir: ex.response }
    ).end();
    dispatch({ type: ADMIN_ERROR, payload: { suppressionlist: "Error getting suppressionlist" } });
  }
  return suppressionlistCache;
};

export const createSuppressionlist = (suppressionRecord) => (dispatch, getState) => {
  LoggerGroup.emit("[i] Creating New Suppression");
  const { searchsuppression = {} } = getState().admin;
  let payload = { action: "OUT" };
  switch ((suppressionRecord.device_type_vch || "").toLowerCase()) {
    case "email":
      payload = {
        to: suppressionRecord.device_vch,
        type: "email",
        from: suppressionRecord.fromemail_vch || "",
        action: "OUT",
      };
      break;

    case "sms":
      payload = {
        from: suppressionRecord.device_vch,
        type: "sms",
        to: suppressionRecord.short_code,
        action: "OUT",
      };
      break;
    case "voice":
      payload = {
        from: suppressionRecord.device_vch,
        type: "voice",
        to: suppressionRecord.caller_id,
        action: "OUT",
      };
      break;
    default:
      dispatch({ type: ADMIN_ERROR, payload: { create: "Error adding Opt out entry" } });
      return new Promise((resolve) => resolve(0));
  }

  return axios
    .post("reporting/optinout", payload)
    .then((res) => {
      const message = get(res, "data.message");
      LoggerGroup.emit(`  [i] Created Suppression ${message} `, { table: suppressionRecord }).end();
      toastr.success(
        "Success",
        `Created new ${suppressionRecord.device_type_vch} opt-out for ${suppressionRecord.device_vch}`
      );
      dispatch(
        getSuppressionlist(true, {
          ...searchsuppression,
          search_device_type: suppressionRecord.device_type_vch,
          search_device_text: suppressionRecord.device_vch,
        })
      );
      return message;
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");

      toastr.error("Oops", error_message);
      LoggerGroup.emit({ warn: "[i] Error adding Opt-out record" }).end();

      dispatch({ type: ADMIN_ERROR, payload: { suppressionlist: "Error adding Opt-out record" } });
    });
};

export const deleteSuppressionlist = (suppressionRecord) => (dispatch, getState) => {
  LoggerGroup.emit(`[i] Deleting suppressionlist[${suppressionRecord.recordid}]`);
  const { admin } = getState();
  const searchsuppression = admin.searchsuppression;

  if (
    !suppressionRecord ||
    !["sms", "email", "voice"].includes(suppressionRecord.device_type) ||
    !(suppressionRecord.recordid > 0)
  ) {
    toastr.error("Oops", "Invalid record deletion request !");
    return;
  }
  const payload = {
    action: "IN",
    type: suppressionRecord.device_type,
    recordid: suppressionRecord.recordid,
    from: suppressionRecord.to_vch,
    to: suppressionRecord.from_vch,
  };
  dispatch(adminLoading("suppressionlist"));

  return axios
    .post(`reporting/optinout`, payload)
    .then((res) => {
      const status = get(res, "data.message");
      LoggerGroup.emit(
        `  [i] Deleted ${suppressionRecord.device_type} record from Suppressionlist [${suppressionRecord.from_vch}-${suppressionRecord.to_vch}]`
      ).end();

      toastr.success(
        "Success",
        `Deleted ${suppressionRecord.device_type} record from Suppressionlist`
      );
      dispatch(getSuppressionlist(true, searchsuppression));
    })
    .catch((err) => {
      const error_message = get(err, "response.data.error");
      toastr.error("Oops", error_message || "[x] Deletion of suppression entry failed.");
      LoggerGroup.emit({
        warn: `[i] Error deleting suppressionlist[${suppressionRecord.device_type}] - [${suppressionRecord.recordid}]`,
      }).end();

      dispatch({
        type: ADMIN_ERROR,
        payload: { delete: "Error deleting record from suppressionlist" },
      });
    });
};

export function escapeRegExp(string) {
  if (string && string !== "") return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return "";
}
